import { useState } from 'react';
import { EntityModal } from '../common';
import { FloorPlan3DView } from './FloorPlan3DView';
import type { BuildSceneOptions } from './three-d';
import type { CanvasItem } from './types';

interface FloorPlan3DModalProps {
  open: boolean;
  onClose: () => void;
  items: CanvasItem[];
  room?: BuildSceneOptions['room'];
  fallbackWidthFt?: number;
  fallbackHeightFt?: number;
  chairSides?: BuildSceneOptions['chairSides'];
  /** Plan name, shown in the modal title. */
  planName?: string;
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId: string;
}

function Toggle({ label, checked, onChange, testId }: ToggleProps) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-gray-300"
        data-testid={testId}
      />
      {label}
    </label>
  );
}

/**
 * The 3D walk-through of a floor plan. Same objects, same feet, same chairs as
 * the 2D canvas — only the camera changes, so what a client is shown here is
 * the plan that was drawn rather than a second drawing kept in step by hand.
 */
export function FloorPlan3DModal({
  open,
  onClose,
  items,
  room,
  fallbackWidthFt,
  fallbackHeightFt,
  chairSides,
  planName,
}: FloorPlan3DModalProps) {
  const [showChairs, setShowChairs] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showWalls, setShowWalls] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [perspective, setPerspective] = useState(true);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={planName ? `3D View — ${planName}` : '3D View'}
      size="xl"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Toggle
            label="Chairs"
            checked={showChairs}
            onChange={setShowChairs}
            testId="toggle-chairs-3d"
          />
          <Toggle
            label="Table numbers"
            checked={showLabels}
            onChange={setShowLabels}
            testId="toggle-labels-3d"
          />
          <Toggle
            label="Walls"
            checked={showWalls}
            onChange={setShowWalls}
            testId="toggle-walls-3d"
          />
          <Toggle label="Grid" checked={showGrid} onChange={setShowGrid} testId="toggle-grid-3d" />
          <Toggle
            label="Perspective"
            checked={perspective}
            onChange={setPerspective}
            testId="toggle-perspective-3d"
          />
          <span className="ml-auto text-xs text-gray-400">Drag to orbit · scroll to zoom</span>
        </div>
        <div className="aspect-[25/16] w-full">
          <FloorPlan3DView
            items={items}
            room={room}
            fallbackWidthFt={fallbackWidthFt}
            fallbackHeightFt={fallbackHeightFt}
            chairSides={chairSides}
            showChairs={showChairs}
            showLabels={showLabels}
            showWalls={showWalls}
            showGrid={showGrid}
            perspective={perspective}
          />
        </div>
      </div>
    </EntityModal>
  );
}
